/**
 * MarketCard — compact card used in the marketplace grid.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DomainBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import type { MarketplaceListing } from '@/api/types';
import { isOfficialProduct } from '@/data/defaultProducts';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber, normalizeDomain } from '@/utils/format';

import { RatingStars } from './RatingStars';

export function MarketCard({ listing }: { listing: MarketplaceListing }) {
  const { t } = useTranslation(['marketplace', 'common']);
  const locale = useLocaleStore((s) => s.locale);
  const normalized = normalizeDomain(listing.domain);
  const domainLabel =
    typeof listing.domain === 'string'
      ? t(`domain.${listing.domain}`, { ns: 'common' })
      : listing.domain.Custom;

  return (
    <Link
      to={`/marketplace/${listing.root_id}`}
      aria-label={`${listing.name} — ${domainLabel}`}
      className="group flex flex-col rounded-lg border border-border bg-surface p-5 shadow-subtle transition-all hover:-translate-y-0.5 hover:border-border-strong hover:shadow-raised"
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-md bg-domain-${normalized}-soft text-domain-${normalized}`}
        >
          <DomainIcon domain={normalized} className="h-5 w-5" />
        </span>
        <span className="font-mono text-[11px] text-ink-subtle">v{listing.version}</span>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <DomainBadge domain={normalized} label={domainLabel} />
          {isOfficialProduct(listing.root_id) && (
            <span
              className="rounded-full border border-amber-300/40 bg-amber-100/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:border-amber-300/30 dark:bg-amber-300/10 dark:text-amber-200"
              data-testid="official-product-badge"
            >
              {t('card.official')}
            </span>
          )}
        </div>
        <h3 className="text-base font-semibold leading-tight text-ink">{listing.name}</h3>
        <p className="line-clamp-2 text-sm text-ink-muted">{listing.tagline}</p>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <RatingStars value={listing.rating} />
        <span className="text-[11px] text-ink-muted">
          {listing.rating.toFixed(1)} · {t('card.reviews', { value: listing.review_count })}
        </span>
      </div>

      <div className="mt-4 flex items-end justify-between border-t border-border pt-3">
        <span className="text-[11px] text-ink-subtle">
          {t('card.installs', { value: formatNumber(listing.installs, locale) })}
        </span>
        <a
          href="mailto:shalev.yoni@gmail.com"
          className="text-sm font-semibold text-spark hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          Contact Us
        </a>
      </div>
    </Link>
  );
}
